<template>
	<date-time-widget v-if="visible && env.touch" :policy="policy" v-model="value"></date-time-widget>
	<div v-else-if="visible" class="fd-temporal">
		<input type="text" v-model="inputValue" autocomplete="off" :class="extraClasses" :disabled="ro" :placeholder="ph" v-bind="tabindexBinding()" @focus="editorFocus" @blur="editorBlur" ref="temporalEditor">
		<drop-down class="fd-temporal-drop-down" ref="dd">
			<symbolic-calendar v-if="symbolicWidget" v-model="inputValue"></symbolic-calendar>
			<template v-else>
				<month-selector v-model:year="year" v-model:month="month"></month-selector>
				<calendar-widget v-model="dateOnlyValue" :month="month" :year="year"></calendar-widget>
			</template>
			<div v-if="temporal.symbolic" class="fd-temporal-symbolic"><label><input type="checkbox" v-model="sym" class="fd-checkbox"> {{ env.msg("date.picker.dynamic") }}</label></div>
		</drop-down>
	</div>
</template>

<script setup lang="ts">
import DateTimeWidget from "./DateTimeWidget.vue";
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {computed, ref, watch} from "vue";
import {injectEnv} from "../../Environment";
import DropDown from "../DropDown.vue";
import CalendarWidget from "./CalendarWidget.vue";
import MonthSelector from "./MonthSelector.vue";
import SymbolicCalendar from "./SymbolicCalendar.vue";
import {SYMBOLIC_LOOSE_REGEX, SYMBOLIC_REGEX} from "./Temporal";
import type {Temporal} from "@t/fd/editor/Temporal";
import {format as formatForHumans, formatDate, parseDate, type ParsedDate} from "@t/util/DateUtil";
import {TimePolicy} from "@t/fd/editor/DateTime";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

const temporal = computed<Temporal>(() => props.part as Temporal);
const dd = ref<InstanceType<typeof DropDown> | null>(null);
const temporalEditor = ref<HTMLElement | null>(null);

const {visible, value, clazz, style, ro, tabindexBinding, getValueOfProperty, setValueOfProperty} = useEditor(props);

const policy = computed(() => {
	switch (temporal.value.timePolicy) {
		case 0:
			return "date";
		case 1:
			return "universal";
		default:
			return "datetime";
	}
});

const intervalStart = computed<boolean>(() => {
	return temporal.value.interval && temporal.value.interval.iam === "start";
});

const intervalEnd = computed<boolean>(() => {
	return temporal.value.interval && temporal.value.interval.iam === "end";
});

type DateTypeForce = "no" | "datetime" | "date";

interface IntervalDuration {
	me?: ParsedDate,
	other?: ParsedDate,
	valid: boolean;
	/** Duration in days (wholeDay=true) or in millis (wholeDay=false) */
	amount: number;
	wholeDay: boolean;
	/** Type of conversion done by calcDuration */
	conversion: DateTypeForce;
}


/**
 * Calculate the duration interval between myIsoDate and the other date.
 * Optionally, it can convert myIsoDate to the given type first to match future (just switched) type.
 **/
function calcDuration(myIsoDate:string, forceMyType:DateTypeForce = "no", forceHours:number = 12, forceMinutes:number = 0):IntervalDuration {
	const other = getValueOfProperty(temporal.value.interval.otherProperty);
	const meParsed = parseDate(myIsoDate);
	const otherParsed = parseDate(other);

	// We cannot determine the interval if one of the dates is missing
	if (meParsed.date == null || otherParsed.date == null) return { valid: false, amount: 0, wholeDay: false, conversion: "no" };

	const id:IntervalDuration = { valid: true, wholeDay: false, amount: 0, me: meParsed, other: otherParsed, conversion: "no" };

	if (meParsed.wholeDay && forceMyType === "datetime") {
		// Convert to datetime
		meParsed.date.setHours(forceHours, forceMinutes, 0, 0);
		meParsed.wholeDay = false;
		id.conversion = "datetime";
	} else if (!meParsed.wholeDay && forceMyType === "date") {
		// Convert to date
		meParsed.date.setHours(12, 0, 0, 0);
		meParsed.wholeDay = true;
		id.conversion = "date";
	}

	if (meParsed.wholeDay) {
		id.wholeDay = true;
		// Normalize both dates to the noon
		meParsed.date.setHours(12, 0, 0, 0);
		otherParsed.date.setHours(12, 0, 0, 0);
		id.amount = Math.round((otherParsed.date.getTime() - meParsed.date.getTime())/86400000);	// amount is in days (rounding is here to handle DST)
	} else {
		if (otherParsed.wholeDay) {
			// If the other day is "all-day" then convert it to "timed"
			otherParsed.date = new Date(otherParsed.date);
			otherParsed.date.setHours(meParsed.date.getHours(), meParsed.date.getMinutes(), meParsed.date.getSeconds());
		}
		id.amount = otherParsed.date.getTime() - meParsed.date.getTime();
	}
	return id;
}

/**
 * What month is displayed in the picker
 */
const month = ref<number | null>(null);
const year = ref<number | null>(null);
const sym = ref<boolean>(false);

const symbolicWidget = computed(() => {
	return sym.value || inputIsSymbolic()
});

/** model value of the input */
const inputValue = ref<string>(fromData(value.value));
const inputValid = ref<boolean>(true);

watch(inputValue, (n: string, o: string) => {
	const p = parseData(n);
	inputValid.value = p.valid;
	const newIsWholeDay = p.wholeDay;
	if (p.valid) {
		// this is valid content. It can be written out
		const original = value.value;
		value.value = p.data;
		if (!p.symbolic) {
			if (original === value.value && n.endsWith("000")) {
				// input changed, but parsed value is the same
				// This solves the edge case when the user types ...15:000 time (with three zeros at the end)
				inputValue.value = fromData(value.value);
			}
			if (intervalStart.value) {
				let dur: IntervalDuration;
				if (newIsWholeDay) dur = calcDuration(original, "date");
				else dur = calcDuration(original, "datetime", p.hours, p.minutes);
				if (dur.valid) {
					const newDate = parseDate(p.data);
					// Set the other end to keep duration
					if (dur.wholeDay) {
						const end = new Date(newDate.date);
						end.setDate(end.getDate() + dur.amount);
						setValueOfProperty(temporal.value.interval.otherProperty, formatDate(end, false));
					} else {
						if (dur.conversion === "datetime") {
							// We've just jumped from date to datetime
							if (dur.amount >= 0) dur.amount += 3600000;	// add hour to the interval
							else dur.amount -= 3600000;
						}
						const end = new Date(newDate.date.getTime() + dur.amount);
						setValueOfProperty(temporal.value.interval.otherProperty, formatDate(end, true));
					}
				}
			} else if (intervalEnd.value) {

			}
		}
	}
});

watch(value, (n, o) => {
	inputValue.value = fromData(n);
});

const dateOnlyValue = computed<string>({
	get(): string {
		const pd = parseDate(value.value);
		const fmt = formatDate(pd.date, false, false);
		return fmt == null ? "" : fmt;
	},
	set(v: string): void {
		const newDate = parseDate(v);
		if (newDate.date == null) {
			// Unset
			value.value = null;
			return;
		}
		const current = parseDate(value.value);
		const dur = intervalStart.value ? calcDuration(value.value) : null;
		if (allowTime() && current.date != null && !current.wholeDay) {
			// Keep existing time
			newDate.date.setHours(current.date.getHours(), current.date.getMinutes(), current.date.getSeconds(), current.date.getMilliseconds());
			value.value = formatDate(newDate.date, true);
			if (dur) {
				const end = new Date(newDate.date.getTime() + dur.amount);
				setValueOfProperty(temporal.value.interval.otherProperty, formatDate(end, true));
			}
		} else {
			// Overwrite without time
			const withTime = temporal.value.timePolicy == TimePolicy.DATETIME;
			if (withTime) {
				// Round time
				newDate.date.setHours(newDate.date.getHours(), 0, 0, 0);
			}
			value.value = formatDate(newDate.date, withTime);
			if (dur) {
				if (dur.wholeDay && !withTime) {
					const end = new Date(newDate.date);
					end.setDate(end.getDate() + dur.amount);
					setValueOfProperty(temporal.value.interval.otherProperty, formatDate(end, false));
				} else {
					let am = dur.amount;
					if (!withTime) am = am * (3600000 * 24);
					const end = new Date(newDate.date.getTime() + dur.amount);
					setValueOfProperty(temporal.value.interval.otherProperty, formatDate(end, withTime));
				}
			}
		}
	}
});

const extraClasses = computed(() => {
	let classes = "";
	if (clazz.value) classes = clazz.value;

	if (inputValid.value) {
		if (intervalStart.value) {
			if (calcDuration(value.value).amount < 0) classes += " invalid";
		} else if (intervalEnd.value) {
			if (calcDuration(value.value).amount > 0) classes += " invalid";
		}
	} else classes += " invalid";

	classes += " fd-temporal-" + policy.value;
	return classes;
});
const ph = computed(() => {
	if (policy.value === "date") return env.formats.date.pattern;
	else if (policy.value === "datetime") return env.formats.datetime.pattern;
	return env.formats.datetime.pattern.replace(/H+:m+/, "($&)");
});

function editorFocus() {
	if (dd.value != null && !ro.value) {
		dd.value.editorFocus(temporalEditor);
	}
}

function editorBlur() {
	if (dd.value != null && !ro.value) {
		dd.value.editorBlur();
	}
}

interface ParsedData {
	valid: boolean,
	/** True if the parsed date is valid and dynamic */
	symbolic?: boolean,
	/** True if the parsed date is valid and all-day */
	wholeDay?: boolean,

	data: string | null,
	year: number,
	month: number,
	day: number,
	hours: number,
	minutes: number
}

interface ValidatedParsedDate extends ParsedDate {
	valid: boolean
}

function allowTime(): boolean {
	return temporal.value.timePolicy > 0;
}

/**
 * Time component is minute, hour or second
 * @param tc time component before normalization (it may be any number)
 */
function normalizeTimeComponent(tc:number, max:number = 59):number {
	let n = Math.floor(Math.abs(tc)) % 100;	// take only the last two digits if bigger number (268 => 68)
	if (n > max) n = n % 10;			// tak only the last digit if n exceeds maximum   (68 => 8 because bigger than 59)
	return n;
}

function parse(str: string): ValidatedParsedDate {
	const result: ValidatedParsedDate = {valid: false, wholeDay: false, date: null};
	if (str == null || str.trim().length === 0) {
		result.date = null;
		result.valid = true;	// empty string is OK
	} else {
		let m = allowTime() ? env.formats.datetime.regex.exec(str) : null; //try datetime regex only if allowed by time policy
		if (m === null) {
			// not date time, try date only
			m = temporal.value.timePolicy === TimePolicy.DATETIME ? null : env.formats.date.regex.exec(str);
			if (m === null) {
				if (allowTime()) m = /^\s*([+\-]?\d{1,2}y)?\s?([+\-]?\d{1,3}m)?\s?([+\-]?\d{1,4}w)?\s?([+\-]?\d{1,5}d)?\s?(\d{1,2}:\d{2})?\s*$/.exec(str);
				else m = /^\s*([+\-]?\d{1,2}y)?\s?([+\-]?\d{1,3}m)?\s?([+\-]?\d{1,4}w)?\s?([+\-]?\d{1,5}d)?()\s*$/.exec(str);

				if (m === null) {
					result.date = null;
				} else {
					// Relative distance
					result.valid = true;

					const dayShift = parseShift(m[4]);
					const weekShift = parseShift(m[3]);
					const monthShift = parseShift(m[2]);
					const yearShift = parseShift(m[1]);
					const time = parseTime(m[5]);
					let d = new Date();
					d = new Date(d.getFullYear() + yearShift,
						d.getMonth() + monthShift,
						d.getDate() + dayShift + 7 * weekShift);

					if (time !== null) {
						d.setHours(time.hours);
						d.setMinutes(time.minutes);
						result.wholeDay = false;
					} else {
						d.setHours(0);
						d.setMinutes(0);
						result.wholeDay = true;
					}
					d.setSeconds(0);
					result.date = d;
				}
			} else {
				result.valid = true;
				let d = new Date(parseInt(m[env.formats.date.y]), parseInt(m[env.formats.date.M]) - 1, parseInt(m[env.formats.date.d]));
				d.setHours(0);
				d.setMinutes(0);
				d.setSeconds(0);
				d.setMilliseconds(0);
				result.date = d;
				result.wholeDay = true;
			}
		} else {
			result.valid = true;
			let d = new Date(parseInt(m[env.formats.datetime.y]), parseInt(m[env.formats.datetime.M]) - 1, parseInt(m[env.formats.datetime.d]));
			d.setHours(normalizeTimeComponent(parseInt(m[env.formats.datetime.H]), 23));
			let minStr = m[env.formats.datetime.m];
			//if (minStr.length === 1) minStr = minStr + "0";
			d.setMinutes(normalizeTimeComponent(parseInt(minStr)));
			d.setSeconds(0);
			d.setMilliseconds(0);
			result.date = d;
			result.wholeDay = false;
		}
	}
	return result;
}


function parseData(d: string): ParsedData {
	let result: Partial<ParsedData> = {valid: false};
	if (temporal.value.symbolic && SYMBOLIC_REGEX.test(d)) {
		result.valid = true;
		result.symbolic = true;
		result.wholeDay = false;
		result.data = d;
	} else {
		result.symbolic = false;
		let dtObj = parse(d);
		result.valid = dtObj.valid;
		result.wholeDay = dtObj.valid && dtObj.wholeDay;
		if (dtObj.valid) {
			if (dtObj.date === null) result.data = "";
			else {
				result.data = formatDate(dtObj.date, !dtObj.wholeDay);
				result.year = dtObj.date.getFullYear();
				result.month = dtObj.date.getMonth();
				result.day = dtObj.date.getDate();
				result.hours = dtObj.date.getHours();
				result.minutes = dtObj.date.getMinutes();
			}
		}
	}
	return result as ParsedData;
}

function parseShift(sh: string): number {
	if (sh == null || sh.length === 0) return 0;
	return parseInt(sh);
}

/** Parse time string "HH:MM" */
function parseTime(time: string): { hours: number, minutes: number } | null {
	if (time == null || time.length === 0) return null;
	const res = {hours: 0, minutes: 0};
	const dot = time.indexOf(':');
	res.hours = parseInt(time.substring(0, dot));
	res.minutes = parseInt(time.substring(dot + 1));
	return res;
}

function inputIsSymbolic() {
	return isSymbolic(inputValue.value);
}

function isSymbolic(v: string | null | undefined): boolean {
	if (v == null) return false;
	return SYMBOLIC_LOOSE_REGEX.test(v);
}

function fromData(d: string | null | undefined): string {
	if (!d) return "";
	// Format for user
	if (SYMBOLIC_REGEX.test(d)) return d;
	let dtObj = parseDate(d);
	if (dtObj.date) {
		return format(dtObj.date, dtObj.wholeDay);
	}
	return '';
}

function format(date: Date, wholeDay: boolean): string {
	return formatForHumans({date, wholeDay}, env);
}

</script>


<style>
.fd-temporal-drop-down {
	color: var(--fg);
	background-color: var(--bg);
}

.fd-temporal-drop-down button {
	box-shadow: none;
	text-shadow: none;
}

.fd-temporal-universal,
.fd-temporal-datetime {
	width: 9.5em;
}

.fd-temporal-date {
	width: 7em;
}

.fd-temporal-symbolic {
	padding: 2px 8px 4px 8px;
	border-top: 1px solid var(--border-color);
}
</style>
